import { GridFilterModel, GridSortModel } from "@mui/x-data-grid-pro";
import { DateTime } from "luxon";
import { FilterSortParams } from "../api";
import { camelCase, capitalize } from "lodash-es";


export const removeKeysFromObject = <T extends object>(
  raw: T, keys: Array<string | number>
) => {
  const stringifiedKeys = keys.map((key) => {
    return typeof key !== 'string' ? key.toString() : key;
  });

  return Object.keys(raw)
    .filter(key => !stringifiedKeys.includes(key))
    .map((key) => ({ [key]: raw[key as keyof T] }))
    .reduce((a, b) => ({ ...a, ...b }), {});
};

const muiToApi: Record<string, string> = {
  'onOrAfter': '__gte',
  'onOrBefore': '__lte',
  '<': '__lt',
  '<=': '__lte',
  '>': '__gt',
  '>=': '__gte',
  'equals': '',
  'isAnyOf': '__in',
  'isEmpty': '__isnull',
  'isNotEmpty': '__isnull',
};

const muiToRTKQ: Record<string, string> = {
  'after': 'Gt',
  'before': 'Lt',
  'onOrAfter': 'Gte',
  'onOrBefore': 'Lte',
  '<': 'Lt',
  '<=': 'Lte',
  '>': 'Gt',
  '>=': 'Gte',
  'equals': 'Iexact',
  'doesNotEqual': 'Ine',
  'contains': 'Icontains',
  'doesNotContain': 'NotIcontains',
  'isAnyOf': 'In',
  'isEmpty': 'Isempty',
  'isNotEmpty': 'Isempty',
};

export const transformMuiDataGridFilterSortToApiModel = <T extends object>({
  sortModel, filterModel, fieldMapping
}: { 
  sortModel?: GridSortModel; 
  filterModel?: GridFilterModel; 
  fieldMapping?: Record<string, string>;
}, rtkqTransform?: boolean): T => {
  const mapField = (field: string) => fieldMapping?.[field] ?? field;
  
  const dataSort = sortModel?.map(
    model => `${model.sort === "asc" ? "" : "-"}${mapField(model.field)}`
  ) as FilterSortParams<T>['order_by'];

  const currentQuery = filterModel ? {
    topology: Array.isArray(filterModel.logicOperator) ? filterModel.logicOperator[0] : filterModel.logicOperator,
    ...filterModel.items.map(item => {
      const muiOperator = item.operator;
      const mappedField = mapField(item.field);
      
      if (muiOperator === 'isEmpty' || muiOperator === 'isNotEmpty') {
        const apiOperator = rtkqTransform ? muiToRTKQ[muiOperator] : muiToApi[muiOperator];
        const value = muiOperator === 'isEmpty' ? true : false;
        return {
          [`${rtkqTransform ? camelCase(mappedField) : mappedField}${apiOperator}`]: value
        };
      }
      
      if (item.value) {
        const value = item.value instanceof Date ? item.value.toISOString() : item.value;

        if (muiOperator && !['eq', 'is', '='].includes(muiOperator)) {
          const apiOperator = rtkqTransform
            ? (muiToRTKQ[muiOperator] ?? capitalize(muiOperator))
            : muiToApi[muiOperator] ?? `__${muiOperator}`;
          return {
            [`${rtkqTransform ? camelCase(mappedField) : mappedField}${apiOperator}`]: value
          };
        }

        return { [rtkqTransform ? camelCase(mappedField) : mappedField]: value };
      }
      return undefined;
    }).filter(a => a).reduce((a, b) => ({ ...a, ...b }), {})
  } : {};

  return { [rtkqTransform ? 'orderBy' : 'order_by']: dataSort, ...currentQuery } as T;
};

let lastUsedId: number | undefined;

export const generateUniqueUnsavedCabinetId = () => {
  let newId = -Math.floor(DateTime.now().toMillis() / 1000);
  if (lastUsedId != null && lastUsedId <= newId) {
    newId = lastUsedId - 1;
  }
  lastUsedId = newId;

  return newId;
};
